// Tiny fetch wrapper for the marketing + tenant SPA → Chill API.
//
// API base URL comes from VITE_API_URL (set by SST at build time, falls back
// to the current public environment, then to prod for localhost dev).

function currentHostname(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.hostname;
}

function defaultApiBase(): string {
  const host = currentHostname();
  if (host === "staging.getchill.ai" || host?.endsWith(".staging.getchill.ai")) {
    return "https://api.staging.getchill.ai";
  }
  return "https://api.getchill.ai";
}

export const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? defaultApiBase();

export function apiUrl(path: string): string {
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

const activeRequests = new Set<AbortController>();

// End the current account's request lifetime before changing its cookie.
// In particular, an old 503 retry must not execute under the new account.
export function invalidateApiRequests(): void {
  for (const controller of activeRequests) controller.abort();
  activeRequests.clear();
}

// Keep multi-request reads and response decoding inside the same account
// lifetime. Callers pass this signal to every request and check it before
// starting follow-up work; invalidation also settles a stalled read promptly.
export async function withApiRequestScope<T>(
  read: (signal: AbortSignal) => Promise<T>,
  callerSignal?: AbortSignal | null,
): Promise<T> {
  const controller = new AbortController();
  const signal = callerSignal
    ? AbortSignal.any([controller.signal, callerSignal])
    : controller.signal;
  activeRequests.add(controller);
  let onAbort: () => void = () => {};
  try {
    signal.throwIfAborted();
    const canceled = new Promise<never>((_, reject) => {
      onAbort = () => reject(signal.reason);
      signal.addEventListener("abort", onAbort, { once: true });
    });
    const result = await Promise.race([read(signal), canceled]);
    signal.throwIfAborted();
    return result;
  } finally {
    signal.removeEventListener("abort", onAbort);
    activeRequests.delete(controller);
  }
}

// fetch() wrapper that transparently retries when the control-plane DB is
// resuming from Aurora auto-pause. The API fails fast with 503
// { error: "database_resuming" } + Retry-After (see lib/control-plane/resume.ts)
// rather than blocking past the CloudFront origin timeout, so the first request
// after an idle period no longer surfaces as an opaque "Failed to fetch". We
// poll back at a short FIXED interval (the resume takes ~20-30s; latency-to-
// recover matters more than backing off a cheap retry).
export async function apiFetch(
  path: string,
  init?: RequestInit,
  opts: { retries?: number; retryDelayMs?: number } = {},
): Promise<Response> {
  const retries = opts.retries ?? 8;
  const fallbackDelay = opts.retryDelayMs ?? 2_000;
  return withApiRequestScope(async (signal) => {
    let r = await fetch(apiUrl(path), { ...init, signal });
    signal.throwIfAborted();
    for (let attempt = 0; r.status === 503 && attempt < retries; attempt += 1) {
      // Honor Retry-After (seconds) when present, else the fixed fallback.
      const ra = Number(r.headers.get("retry-after"));
      const delay = Number.isFinite(ra) && ra > 0 ? ra * 1_000 : fallbackDelay;
      await waitForRetry(delay, signal);
      signal.throwIfAborted();
      r = await fetch(apiUrl(path), { ...init, signal });
      signal.throwIfAborted();
    }
    return r;
  }, init?.signal);
}

function waitForRetry(delay: number, signal?: AbortSignal | null): Promise<void> {
  if (!signal) return new Promise((resolve) => setTimeout(resolve, delay));
  if (signal.aborted) return Promise.reject(signal.reason);

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function publicApexHost(): string {
  const configured = import.meta.env.VITE_PUBLIC_APEX_HOST as string | undefined;
  if (configured) return configured.replace(/^https?:\/\//, "").replace(/\/$/, "");

  const host = currentHostname();
  if (host === "staging.getchill.ai" || host?.endsWith(".staging.getchill.ai")) {
    return "staging.getchill.ai";
  }
  return "getchill.ai";
}

export function tenantPublicUrl(slug: string): string {
  return `https://${slug}.${publicApexHost()}`;
}

export function inboundAgentEmail(): string {
  const configured = import.meta.env.VITE_INBOUND_AGENT_EMAIL as string | undefined;
  if (configured) return configured;
  return publicApexHost() === "staging.getchill.ai"
    ? "agent@staging.inbound.getchill.ai"
    : "agent@inbound.getchill.ai";
}

// Tenant assets (photos / audio / video) live under /sites/<slug>/... — in
// dev they're served by Vite from apps/web/public/sites/<slug> (a symlink to
// the local seed/<slug> directory); in prod the build env replaces VITE_MEDIA_URL
// with the CloudFront/S3 origin (e.g. https://media.getchill.ai).
export const MEDIA_BASE: string =
  (import.meta.env.VITE_MEDIA_URL as string | undefined) ?? "";

export function mediaUrl(path: string): string {
  if (!path) return path;
  // Already absolute? leave alone.
  if (/^https?:\/\//i.test(path)) return path;
  return `${MEDIA_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}
